import {Component, Type} from '@angular/core';
import {Step} from '../application-flow/steps/step';
import {ActivatedRoute, Data} from '@angular/router';
import {FileDetails} from '../model/file-details.model';
import {EmployeeType} from '../model/employee-type.model';
import {FileInteractor} from '../file-upload/file.interactor';
import {UserInteractor} from '../interactors/user.interactor';
import {Keys, StorageService} from '../service/storage.service';
import {UserAddressInteractor} from '../interactors/user-address.interactor';
import {BankAccountInteractor} from '../interactors/bank-account.interactor';
import {UploadDocumentsStep} from '../application-flow/steps/upload-documents.step';
import {AddressProofType, AddressProofTypeModel} from '../model/address-proof-type.model';
import {LoanApplicationFlowNavigator} from '../application-flow/loan-application-flow.navigator';
import {LivelihoodInformationInteractor} from '../interactors/livelihood-information.interactor';
import {AbstractApplicationFlowComponent} from '../application-flow/abstract-application-flow.component';
import {
    LoanApplicationDocumentType,
    LoanApplicationDocumentTypeModel
} from '../model/loan-application-document-type.model';
import {executeWithLoader, LoaderStatus} from '../utils/loader.util';
import {VerificationStatus} from '../model/user.model';

interface UploadFileForm {
    pan: FileDetails[];
    photo: FileDetails[];
    address_proof: FileDetails[];
    salary: FileDetails[];
    itr: FileDetails[];
    other_document: FileDetails[];
    bank: FileDetails[];
    employeeType: EmployeeType;
    addressProofType: AddressProofType;
}

class Validation {
    error: string;
    isInValid: boolean;

    constructor(error: string, isInValid: boolean) {
        this.error = error;
        this.isInValid = isInValid;
    }

    set(error: string, isInValid: boolean): void {
        this.error = error;
        this.isInValid = isInValid;
    }

    reset(): void {
        this.error = undefined;
        this.isInValid = false;
    }
}

interface UploadFileFormValidator {
    pan: Validation;
    photo: Validation;
    address_proof: Validation;
    salary: Validation;
    itr: Validation;
    other_document: Validation;
    bank: Validation;
    addressProofType: Validation;
    employeeType: Validation;
}

interface Spinner {
    isVisible: boolean;
    target: string;
}

@Component({
    selector: 'app-upload-documents',
    templateUrl: './upload-documents.component.html',
    styleUrls: ['./upload-documents.component.scss']
})
export class UploadDocumentsComponent extends AbstractApplicationFlowComponent {
    public static readonly MaskedAadhaarFileRegExp = /^.{1,200}\.pdf$/;
    public uploadedFileForm: UploadFileForm;
    public uploadFileFormValidator: UploadFileFormValidator;
    public spinner: Spinner = {isVisible: false, target: undefined};
    public readonly EmployeeType = EmployeeType;
    public readonly AddressProofType = AddressProofType;
    public readonly AddressProofTypeModel = AddressProofTypeModel;
    public readonly LoanApplicationDocumentType = LoanApplicationDocumentType;
    public panVerificationStatus: VerificationStatus;
    public photoVerificationStatus: VerificationStatus;
    public loaderStatus = new LoaderStatus();

    constructor(loanApplicationFlowNavigator: LoanApplicationFlowNavigator,
                private fileInteractor: FileInteractor,
                private userInteractor: UserInteractor,
                private userAddressInteractor: UserAddressInteractor,
                private bankAccountInteractor: BankAccountInteractor,
                private livelihoodInformationInteractor: LivelihoodInformationInteractor,
                private activatedRoute: ActivatedRoute, private storageService: StorageService) {
        super(loanApplicationFlowNavigator);
        activatedRoute.data.subscribe((data: Data) => {
            this.populateFile(data.uploadDocumentsData);
            this.panVerificationStatus = data.uploadDocumentsData ? data.uploadDocumentsData.panVerificationStatus : undefined;
            this.photoVerificationStatus = data.uploadDocumentsData ? data.uploadDocumentsData.photoVerificationStatus : undefined;
        });
    }

    step(): Type<Step> {
        return UploadDocumentsStep;
    }

    public async uploadDocument(loanApplicationDocumentType: LoanApplicationDocumentType, event: any): Promise<void> {
        const file = event.target.files[0];
        this.spinner = {isVisible: true, target: loanApplicationDocumentType};
        try {
            if (this.isValidFor(file, loanApplicationDocumentType, this.uploadedFileForm.addressProofType)) {
                const fileId = await this.fileInteractor.upload(file);
                switch (loanApplicationDocumentType) {
                    case LoanApplicationDocumentType.PAN:
                        await this.userInteractor.addPanDocument([fileId]);
                        this.uploadedFileForm.pan.push(new FileDetails(fileId, file.name));
                        this.uploadFileFormValidator.pan.reset();
                        break;
                    case LoanApplicationDocumentType.PHOTO:
                        await this.userInteractor.addProfilePhoto([fileId]);
                        this.addFileToUploadedFileForm(LoanApplicationDocumentType.PHOTO, fileId, file.name);
                        this.resetFileFormValidator(LoanApplicationDocumentType.PHOTO);
                        break;
                    case LoanApplicationDocumentType.ADDRESS_PROOF:
                        await this.userAddressInteractor.addAddressProof([fileId], this.uploadedFileForm.addressProofType);
                        this.addFileToUploadedFileForm(LoanApplicationDocumentType.ADDRESS_PROOF, fileId, file.name);
                        this.resetFileFormValidator(LoanApplicationDocumentType.ADDRESS_PROOF);
                        break;
                    case LoanApplicationDocumentType.BANK_STATEMENT:
                        await this.bankAccountInteractor.addBankStatementDocuments([fileId]);
                        this.addFileToUploadedFileForm(LoanApplicationDocumentType.BANK_STATEMENT, fileId, file.name);
                        this.resetFileFormValidator(LoanApplicationDocumentType.BANK_STATEMENT);
                        break;
                    case LoanApplicationDocumentType.ITR:
                        await this.livelihoodInformationInteractor.updateLivelihoodDocuments(LoanApplicationDocumentType.ITR, [fileId]);
                        this.addFileToUploadedFileForm(LoanApplicationDocumentType.ITR, fileId, file.name);
                        this.resetFileFormValidator(LoanApplicationDocumentType.ITR);
                        break;
                    case LoanApplicationDocumentType.SALARY_SLIP:
                        await this.livelihoodInformationInteractor.updateLivelihoodDocuments(LoanApplicationDocumentType.SALARY_SLIP, [fileId]);
                        this.addFileToUploadedFileForm(LoanApplicationDocumentType.SALARY_SLIP, fileId, file.name);
                        this.resetFileFormValidator(LoanApplicationDocumentType.SALARY_SLIP);
                        break;
                    case LoanApplicationDocumentType.OTHER_DOCUMENT:
                        await this.livelihoodInformationInteractor.updateLivelihoodDocuments(LoanApplicationDocumentType.OTHER_DOCUMENT, [fileId]);
                        this.addFileToUploadedFileForm(LoanApplicationDocumentType.OTHER_DOCUMENT, fileId, file.name);
                        this.resetFileFormValidator(LoanApplicationDocumentType.OTHER_DOCUMENT);
                        this.resetFileFormValidator(LoanApplicationDocumentType.OTHER_DOCUMENT);
                        break;
                }
            }
        } finally {
            this.spinner = {isVisible: false, target: undefined};
        }
    }

    public async onSubmit() {
        await executeWithLoader(this.loaderStatus, async () => {
            const isValid = this.validateForm().toString();
            this.storageService.saveItem(Keys.IS_UPLOAD_DOCUMENT_COMPLETED, isValid);
            await this.nextPage();
        });
    }

    validateForm(): boolean {
        this.excludeNotRequiredFields();
        let isValid = true;
        Object.keys(this.uploadedFileForm).forEach(key => {
            if (key === 'addressProofType' || key === 'employeeType' || key === 'photoVerificationStatus' || key === 'panVerificationStatus') {
                return;
            }
            this.uploadedFileForm[key].length ? this.uploadFileFormValidator[key].reset() : this.uploadFileFormValidator[key].set(`Please upload ${LoanApplicationDocumentTypeModel.getPrintableNameFor(key)} to continue.`, true);
            if (this.uploadedFileForm[key].length === 0) {
                isValid = false;
            }
        });
        return isValid;
    }

    excludeNotRequiredFields(): void {
        switch (this.uploadedFileForm.employeeType) {
            case EmployeeType.Professional :
                delete this.uploadedFileForm[LoanApplicationDocumentType.SALARY_SLIP];
                delete this.uploadedFileForm.other_document;
                break;
            case EmployeeType.SelfEmployed:
                delete this.uploadedFileForm[LoanApplicationDocumentType.SALARY_SLIP];
                break;
            case  EmployeeType.Salaried:
                delete this.uploadedFileForm.other_document;
                break;
        }
    }

    isValidFor(file: any, loanApplicationDocumentType: LoanApplicationDocumentType, addressProofType: AddressProofType): boolean {
        const sizeValidation = file.size <= 10000000;
        let typeValidation = false;
        if (loanApplicationDocumentType === LoanApplicationDocumentType.ADDRESS_PROOF && addressProofType === AddressProofType.aadhaar) {
            typeValidation = UploadDocumentsComponent.MaskedAadhaarFileRegExp.test(file.name);
            if (!typeValidation) {
                this.uploadFileFormValidator[loanApplicationDocumentType].set(`Please try uploading (.pdf) file`, true);
            }
        } else {
            const acceptableFormats = (loanApplicationDocumentType === LoanApplicationDocumentType.BANK_STATEMENT ||
            loanApplicationDocumentType === LoanApplicationDocumentType.ITR) ? ['pdf'] : ['jpg', 'jpeg', 'png', 'pdf'];

            const acceptableFormatsAsRegExp = acceptableFormats.map(format => new RegExp(`^.{1,200}\.${format}$`));
            typeValidation = acceptableFormatsAsRegExp.some(format => format.test(file.name.toLowerCase()));
            if (!typeValidation) {
                this.uploadFileFormValidator[loanApplicationDocumentType].set(`File type not supported, please try uploading (${acceptableFormats.join(',')})`, true);
            }
        }

        const fileExist = this.isFileExistFor(file, loanApplicationDocumentType);
        if (!sizeValidation) {
            this.uploadFileFormValidator[loanApplicationDocumentType].set(`Please upload file with filesize within 10MB`, true);
        }

        if (fileExist) {
            this.uploadFileFormValidator[loanApplicationDocumentType].set(`File already exists in the system, please try uploading a different file`, true);
        }
        return (sizeValidation && typeValidation && !fileExist);
    }

    isFileExistFor(file: any, loanApplicationDocumentType: LoanApplicationDocumentType): boolean {
        return this.uploadedFileForm[loanApplicationDocumentType].some(uploadedFile => {
            if (uploadedFile.fileName === file.name) {
                return true;
            }
        });
    }

    public async removeFile(type: LoanApplicationDocumentType, fileId: string): Promise<void> {
        switch (type) {
            case LoanApplicationDocumentType.PAN:
                await this.userInteractor.removePanFile(fileId);
                this.uploadedFileForm.pan.splice(this.uploadedFileForm.pan.findIndex(file => file.fileId === fileId), 1);
                break;
            case LoanApplicationDocumentType.PHOTO:
                await this.userInteractor.removePhotoFile(fileId);
                this.removeFileFromUploadedFileForm(LoanApplicationDocumentType.PHOTO, fileId);
                break;
            case LoanApplicationDocumentType.ADDRESS_PROOF:
                await this.userAddressInteractor.removeProofDocumentFile(fileId);
                this.removeFileFromUploadedFileForm(LoanApplicationDocumentType.ADDRESS_PROOF, fileId);
                break;
            case LoanApplicationDocumentType.BANK_STATEMENT:
                await this.bankAccountInteractor.removeBankStatementFile(fileId);
                this.removeFileFromUploadedFileForm(LoanApplicationDocumentType.BANK_STATEMENT, fileId);
                break;
            case LoanApplicationDocumentType.ITR:
                await this.livelihoodInformationInteractor.removeLivelihoodDocuments(LoanApplicationDocumentType.ITR, fileId);
                this.removeFileFromUploadedFileForm(LoanApplicationDocumentType.ITR, fileId);
                break;
            case LoanApplicationDocumentType.SALARY_SLIP:
                await this.livelihoodInformationInteractor.removeLivelihoodDocuments(LoanApplicationDocumentType.SALARY_SLIP, fileId);
                this.removeFileFromUploadedFileForm(LoanApplicationDocumentType.SALARY_SLIP, fileId);
                break;
            case LoanApplicationDocumentType.OTHER_DOCUMENT:
                await this.livelihoodInformationInteractor.removeLivelihoodDocuments(LoanApplicationDocumentType.OTHER_DOCUMENT, fileId);
                this.removeFileFromUploadedFileForm(LoanApplicationDocumentType.OTHER_DOCUMENT, fileId);
                break;
        }
    }

    private populateFile(uploadDocumentFileNames: UploadFileForm): void {
        this.uploadedFileForm = uploadDocumentFileNames;
        this.uploadFileFormValidator = {
            pan: new Validation(undefined, false),
            address_proof: new Validation(undefined, false),
            addressProofType: new Validation(undefined, false),
            employeeType: new Validation(undefined, false),
            bank: new Validation(undefined, false),
            itr: new Validation(undefined, false),
            other_document: new Validation(undefined, false),
            photo: new Validation(undefined, false),
            salary: new Validation(undefined, false)
        };
    }

    private addFileToUploadedFileForm(type: string, fileId: string, name: any) {
        this.uploadedFileForm[type].push(new FileDetails(fileId, name));
    }

    private resetFileFormValidator(type: string) {
        this.uploadFileFormValidator[type].reset();
    }

    private removeFileFromUploadedFileForm(type: string, fileId: string) {
        this.uploadedFileForm[type].splice(this.uploadedFileForm[type]
            .findIndex(file => file.fileId === fileId), 1);
    }

    public isVerified(verificationStatus: VerificationStatus) {
        return verificationStatus === VerificationStatus.VERIFIED;
    }
}
